import { AppError } from "../api/problem";
import type { BillingPlansData, PlanOffer, PortalAccessLevel } from "../api/contract";
import { getBillingPlans } from "../api/endpoints";

class PlansStore {
  currentOrgId = $state<string | null>(null);
  data = $state<BillingPlansData | null>(null);
  loading = $state(false);
  error = $state<AppError | null>(null);

  #controller: AbortController | null = null;

  get currentPlan(): BillingPlansData["currentPlan"] {
    return this.data?.currentPlan ?? null;
  }

  get currentAccessLevel(): PortalAccessLevel | null {
    return this.data?.currentAccessLevel ?? null;
  }

  get offers(): PlanOffer[] {
    return this.data?.offers ?? [];
  }

  /** The current plan's own catalog entry, when the endpoint carries one. */
  get currentOffer(): PlanOffer | null {
    return this.data?.current ?? null;
  }

  setOrg(orgId: string | null): void {
    if (orgId === this.currentOrgId) return;
    this.#abort();
    this.currentOrgId = orgId;
    this.data = null;
    this.error = null;
  }

  async load(orgId: string): Promise<void> {
    this.#abort();
    const controller = new AbortController();
    this.#controller = controller;
    this.loading = true;
    this.error = null;
    try {
      const data = await getBillingPlans(orgId, controller.signal);
      if (controller.signal.aborted) return;
      this.currentOrgId = orgId;
      this.data = data;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      if (error instanceof AppError) this.error = error;
      else throw error;
    } finally {
      if (this.#controller === controller) {
        this.loading = false;
        this.#controller = null;
      }
    }
  }

  reload(): void {
    if (this.currentOrgId) void this.load(this.currentOrgId);
  }

  #abort(): void {
    this.#controller?.abort();
    this.#controller = null;
    this.loading = false;
  }
}

export const plans = new PlansStore();
